import React, { useState } from 'react';
import { uploadCsv } from '../lib/api';

interface UploadResult {
  inserted: number;
  updated: number;
  unmatchedNames: string[];
}

interface UploadSectionProps {
  onUploadComplete: (result: UploadResult, type: string) => void;
}

const UPLOAD_TYPES = [
  { value: 'lemon', label: 'Lemon Time Billing (horas)' },
  { value: 'buk-vacation', label: 'BUK - Vacaciones' },
  { value: 'buk-medical', label: 'BUK - Licencias médicas' },
  { value: 'dias-adm', label: 'Control - Días administrativos' },
];

export function UploadSection({ onUploadComplete }: UploadSectionProps) {
  const [type, setType] = useState('lemon');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    setIsUploading(true);
    setError(null);

    try {
      const result = await uploadCsv(type, file);
      onUploadComplete(result, type);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al subir el archivo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleUpload(files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <label htmlFor="upload-type" className="block text-sm font-medium text-slate-700 mb-1">
          Tipo de archivo
        </label>
        <select
          id="upload-type"
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-fycom-blue focus:border-fycom-blue outline-none bg-white"
        >
          {UPLOAD_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
      </div>

      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition
          ${isDragging ? 'border-fycom-blue bg-blue-50' : 'border-slate-300 bg-slate-50'}
        `}
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          className="hidden"
          id="csv-upload"
        />
        <label htmlFor="csv-upload" className="cursor-pointer block">
          <div className="text-slate-600 mb-2">
            {isUploading ? 'Procesando...' : 'Arrastra un archivo CSV aquí o haz clic para seleccionar'}
          </div>
          <div className="text-sm text-slate-400">
            Formatos soportados: BUK, Lemon Time Billing, Días administrativos
          </div>
        </label>
      </div>

      {error && (
        <div className="mt-4 bg-red-50 text-fycom-red px-4 py-2 rounded-lg text-sm">
          {error}
        </div>
      )}
    </div>
  );
}
